import React, {useState} from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import TableCell from 'components/table/TableCell';
import {HrDocument} from 'types/hrDocument';
import Table from 'components/table/Table';
import {SearchType} from 'types/search';
import {HrDocumentThunks} from './hrDocumentThunks';
import {
  setIsEditing,
  setHrDocument,
  setActiveTab,
} from 'features/hrDocument/hrDocumentSlice';
import BlueButton from 'components/BlueButton';
import PlusIcon from 'assets/images/icons/PlusIcon';
import {useDropdownActionMenu} from 'hooks/useDropdownActionMenu';
import DotsMenu from 'components/DotsMenu';
import TrashIcon from 'assets/images/icons/TrashIcon';
import StatusBadge from 'components/StatusBadge';
import usePermissions from 'hooks/usePermissions';

const HrDocumentTable: React.FC<{openDrawer: () => void}> = ({openDrawer}) => {
  const hrDocuments = useSelector(
    (state: RootState) => state.hrDocument.hrDocuments,
  );
  const dispatch = useDispatch<AppDispatch>();
  const pagy = useSelector((state: RootState) => state.hrDocument.pagy);
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const {checkPermission} = usePermissions();
  const activeTab = useSelector(
    (state: RootState) => state.hrDocument.activeTab,
  );

  const loading = useSelector(
    (state: RootState) => state.hrDocument.status === 'loading',
  );

  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    name: true,
    description: true,
    status: true,
  });

  interface VisibleColumnsType {
    id: boolean;
    name: boolean;
    description: boolean;
    status: boolean;
  }

  const searchByOptions = [{label: 'Name', value: 'name', operator: 'like'}];

  const columnOptions = [
    {label: 'ID', value: 'id', isVisible: visibleColumns.id},
    {label: 'Name', value: 'name', isVisible: visibleColumns.name},
    {label: 'Status', value: 'status', isVisible: visibleColumns.status},
    {
      label: 'Description',
      value: 'description',
      isVisible: visibleColumns.description,
    },
  ];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    if (activeTab === 'Active' || activeTab === 'Inactive') {
      search.where.status = {
        operator: '==',
        value: activeTab.toLowerCase(),
      };
    } else {
      delete search.where.status;
    }
    await dispatch(
      HrDocumentThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prev => ({
        ...prev,
        [column]: !prev[column as keyof VisibleColumnsType],
      }));
    }
  };

  const handleOpenDrawer = (id: string) => async () => {
    openDrawer();
    await dispatch(HrDocumentThunks.show(id));
  };

  const handleDeactivate = (hrDocumentId: string) => {
    dispatch(HrDocumentThunks.deactivate(hrDocumentId));
    setDotsMenuIsOpen(null);
  };

  const handleReactivate = (hrDocumentId: string) => {
    dispatch(HrDocumentThunks.activate(hrDocumentId));
    setDotsMenuIsOpen(null);
  };

  const handleOpenNewHrDocumentDrawer = () => {
    dispatch(
      setHrDocument({
        id: '',
        name: '',
        description: '',
        status: '',
      }),
    );
    dispatch(setIsEditing(true));
    openDrawer();
  };
  const handleDelete = async (id: string) => {
    await dispatch(HrDocumentThunks.delete(id));
    setDotsMenuIsOpen(null);
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      pageable={true}
      recordsName={'Documents'}
      activeTab={activeTab}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={['All', 'Active', 'Inactive']}
      searchable={true}
      columns={true}
      columnOptions={columnOptions}
      onColumnToggle={handleColumnToggle}
      loading={loading}
      searchByOptions={searchByOptions}
      blueButton={
        checkPermission('create', 'HR') ? (
          <BlueButton
            onClick={handleOpenNewHrDocumentDrawer}
            label=""
            icon={<PlusIcon />}
          />
        ) : null
      }>
      <thead>
        <tr>
          {visibleColumns.id && <TableHeader label="ID" sortBy="id" />}
          {visibleColumns.name && <TableHeader label="Name" sortBy="name" />}
          {visibleColumns.status && (
            <TableHeader label="Status" sortBy="status" />
          )}
          {visibleColumns.description && <TableHeader label="Description" />}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {hrDocuments.map((hrDocument: HrDocument) => {
          const userOptions = [];

          if (checkPermission('update', 'HR')) {
            userOptions.push({
              label: 'Edit',
              onClick: handleOpenDrawer(hrDocument.id),
              hoverClass: 'hover:bg-cyan-100',
            });

            if (hrDocument.status === 'Active') {
              userOptions.push({
                label: 'Deactivate',
                onClick: () => handleDeactivate(hrDocument.id),
                hoverClass: 'hover:bg-red-100 hover:text-red-800',
              });
            }
            if (hrDocument.status === 'Inactive') {
              userOptions.push({
                label: 'Activate',
                onClick: () => handleReactivate(hrDocument.id),
                hoverClass: 'hover:bg-cyan-100',
              });
            }
          }

          if (checkPermission('delete', 'HR')) {
            userOptions.push({
              label: 'Delete',
              onClick: () => handleDelete(hrDocument.id),
              icon: <TrashIcon />,
              hoverClass: 'hover:bg-red-100 hover:text-red-800',
            });
          }

          return (
            <TableRow key={hrDocument.id}>
              {visibleColumns.id && <TableCell>{hrDocument.id}</TableCell>}
              {visibleColumns.name && (
                <TableCell>
                  <div className=" relative group">
                    <div className="w-full flex items-center gap-x-3">
                      <div className="flex-grow">
                        {checkPermission('update', 'HR') ? (
                          <span
                            className="text-sm cursor-pointer font-medium dark:text-neutral-200 underline text-cyan-600"
                            onClick={handleOpenDrawer(hrDocument.id)}>
                            {hrDocument.name}
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                            {hrDocument.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>
              )}
              {visibleColumns.status && (
                <TableCell>
                  <StatusBadge status={hrDocument.status} />
                </TableCell>
              )}
              {visibleColumns.description && (
                <TableCell className="whitespace-nowrap overflow-hidden text-ellipsis">
                  <div
                    style={{maxWidth: '150px'}}
                    className="whitespace-nowrap overflow-hidden text-ellipsis">
                    {hrDocument.description}
                  </div>
                </TableCell>
              )}
              <TableCell>
                <DotsMenu options={userOptions} />
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export default HrDocumentTable;
